/**
 * Footer pager for a segment's unit list (Slice 8).
 *
 * Client-side only: every unit is already in memory, so a page is a slice and
 * turning one costs no request. Follows the range-plus-prev/next shape the
 * other client-paged admin lists use (`listings/ListingsPagination`,
 * `components/admin/InventorySection`) rather than inventing a second pager
 * dialect — but stays local, because that one names Listings in its copy.
 *
 * The range line shows even on a single page, since "1–7 von 7" is how a
 * reader confirms a filter did what they meant. The prev/next controls appear
 * only when there is somewhere to go.
 */
import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const NAV =
  "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40";

export default function UnitsPagination({
  page,
  pageCount,
  total,
  from,
  to,
  onPrev,
  onNext,
}) {
  // Nothing found: the segment's own empty state is the whole message.
  if (!total) return null;

  return (
    <div
      data-testid="units-pagination"
      className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3"
    >
      <span className="text-[11.5px] text-muted-foreground" data-testid="units-pagination-range">
        {from}–{to} von {total} {total === 1 ? "Einheit" : "Einheiten"}
      </span>

      {pageCount > 1 ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onPrev}
            disabled={page === 0}
            className={NAV}
            aria-label="Vorherige Seite"
            data-testid="units-page-prev"
          >
            <ChevronLeft aria-hidden="true" className="h-4 w-4" />
          </button>
          <span className="text-[11.5px] text-muted-foreground" data-testid="units-page-status">
            Seite {page + 1} von {pageCount}
          </span>
          <button
            type="button"
            onClick={onNext}
            disabled={page >= pageCount - 1}
            className={NAV}
            aria-label="Nächste Seite"
            data-testid="units-page-next"
          >
            <ChevronRight aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
